import React, { useEffect, useRef } from 'react';
import { useInView } from '@src/hooks/useInView';
import { COLOR } from '@src/styles/color';
import useUserInfo from '@src/hooks/useTtsUser';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Button from '@src/new-components/Button';
import Typography from '@src/new-components/Typography';
import { AddRounded } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import ROUTES from '@src/constants/route';
import { VOICE_CLONING_TYPE } from '@src/constants/voiceCloning';
import noDataImage from '@src/assets/images/no-data.svg';
import { VOICE_ITEM_VARIANT } from '@src/constants/voiceLibrary';
import VoiceItem from '../VoiceItem';
import { StyledVoiceList, StyledNoDataVoiceList } from './index.style';
import { StyledVoiceCardItem } from '../VoiceItem/VoiceCardItem/index.style';
import FeaturedVoiceSkeleton from '../LoadingSkeleton/FeaturedVoice';
import VoiceSkeleton from '../LoadingSkeleton/VoiceSkeleton';

const VoiceList = ({
  voices = [],
  isCard = false,
  isLoadMore = false,
  hasMore = false,
  loadMore,
  isLoading = false,
  isTrending = false,
  isVbee = false,
  section,
  onChangeVoice,
  onClose,
  isMyVoice = false,
  isApplyPreset = false,
  isInitialLoading = false,
  isCommunity = false,
  isDubbing,
  onFetchFavouriteVoices,
  isDialog,
  favouriteVoiceCodes,
  isFavourite = false,
  onFavoriteToggleRequest,
  onHandleFavoriteProcessingComplete,
  isFiltering = false,
  onErrorProcessingFavouriteVoices,
  activeVoiceCode,
  isLeaderboard = false,
  prizes,
  isOpenFromSpeakerSettings = false,
  onCloseWhenCreateVoice,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const { ttsUser } = useUserInfo();
  const { user } = useSelector((state) => state.auth);
  const { usingPackage } = useSelector((state) => state.user);
  if (isLoadMore) {
    const { ref, inView } = useInView({
      rootMargin: '1000px',
    });
    const listRef = useRef();
    const dataVoices = {
      items: voices,
      isLoading,
      loadMore,
      hasMore,
    };

    useEffect(() => {
      listRef.current = dataVoices;
    }, [dataVoices]);

    useEffect(() => {
      if (
        listRef.current.items.length &&
        inView &&
        !listRef.current.isLoading &&
        listRef.current.hasMore
      ) {
        listRef.current.loadMore();
      }
    }, [inView]);
    const handleCreateVoice = () => {
      history.push({
        pathname: ROUTES.VOICE_CLONING_VOICES,
        state: { type: VOICE_CLONING_TYPE.CREATE },
      });
      if (isOpenFromSpeakerSettings && onCloseWhenCreateVoice) {
        onCloseWhenCreateVoice();
      }
    };

    if (voices?.length === 0 && !isLoading) {
      return (
        <StyledNoDataVoiceList>
          <img style={{ height: '150px' }} src={noDataImage} alt="no data" />
          <Typography variant="body-14-medium" color={COLOR.label2[64]}>
            {(() => {
              if (isFavourite) {
                return isFiltering
                  ? t('noDataVoiceList')
                  : t('emptyFavouriteVoice');
              }
              return t('noDataVoiceList');
            })()}
          </Typography>
          {!isFavourite && (
            <Button
              variant="outlineSmall"
              startIcon={<AddRounded sx={{ width: '24px', height: '24px' }} />}
              customStyle={{
                padding: '8px 16px',
                borderRadius: '8px',
                height: '40px',
                marginTop: '16px',
              }}
              onClick={handleCreateVoice}
            >
              <Typography variant="body-14-medium">
                {t('createVoice')}
              </Typography>
            </Button>
          )}
        </StyledNoDataVoiceList>
      );
    }

    return (
      <StyledVoiceList className={isCard ? 'card' : 'list'}>
        {voices?.map((voice) => (
          <VoiceItem
            key={voice.id}
            voice={voice}
            isCard={isCard}
            variant={isCard ? VOICE_ITEM_VARIANT.CARD : VOICE_ITEM_VARIANT.LIST}
            usingPackage={usingPackage}
            ttsUser={ttsUser}
            user={user}
            isTrending={isTrending}
            isVbee={isVbee}
            section={section}
            onChangeVoice={onChangeVoice}
            onClose={onClose}
            isMyVoice={isMyVoice}
            isApplyPreset={isApplyPreset}
            isCommunity={isCommunity}
            isDubbing={isDubbing}
            onFetchFavouriteVoices={onFetchFavouriteVoices}
            isDialog={isDialog}
            favouriteVoiceCodes={favouriteVoiceCodes}
            isFavourite={isFavourite}
            onFavoriteToggleRequest={onFavoriteToggleRequest}
            onHandleFavoriteProcessingComplete={
              onHandleFavoriteProcessingComplete
            }
            onErrorProcessingFavouriteVoices={onErrorProcessingFavouriteVoices}
            activeVoiceCode={activeVoiceCode}
            prizes={prizes}
            isOpenFromSpeakerSettings={isOpenFromSpeakerSettings}
          />
        ))}
        {isLoading && (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            {Array.from({ length: 5 }).map(() => (
              <VoiceSkeleton />
            ))}
          </div>
        )}
        <div ref={ref} />
      </StyledVoiceList>
    );
  }

  return (
    <StyledVoiceList
      isLeaderboard={isLeaderboard}
      className={isCard ? 'card' : 'list'}
    >
      {isInitialLoading && (
        <>
          {Array.from({ length: 5 }).map(() => (
            <StyledVoiceCardItem>
              <FeaturedVoiceSkeleton />
            </StyledVoiceCardItem>
          ))}
        </>
      )}
      {!isInitialLoading &&
        voices?.map((voice) => (
          <VoiceItem
            key={voice.id}
            voice={voice}
            isCard={isCard}
            variant={isCard ? VOICE_ITEM_VARIANT.CARD : VOICE_ITEM_VARIANT.LIST}
            usingPackage={usingPackage}
            ttsUser={ttsUser}
            user={user}
            isTrending={isTrending}
            isVbee={isVbee}
            section={section}
            onChangeVoice={onChangeVoice}
            onClose={onClose}
            isMyVoice={isMyVoice}
            isApplyPreset={isApplyPreset}
            isCommunity={isCommunity}
            isDubbing={isDubbing}
            onFetchFavouriteVoices={onFetchFavouriteVoices}
            isDialog={isDialog}
            favouriteVoiceCodes={favouriteVoiceCodes}
            onFavoriteToggleRequest={onFavoriteToggleRequest}
            onHandleFavoriteProcessingComplete={
              onHandleFavoriteProcessingComplete
            }
            onErrorProcessingFavouriteVoices={onErrorProcessingFavouriteVoices}
            activeVoiceCode={activeVoiceCode}
            isLeaderboard={isLeaderboard}
            prizes={prizes}
            isOpenFromSpeakerSettings={isOpenFromSpeakerSettings}
          />
        ))}
    </StyledVoiceList>
  );
};

export default VoiceList;
